// Pattern for adding future trackers:
//
// Method A — park in index.html (activated on consent grant):
//   <script type="text/plain" data-consent="analytics"
//           src="https://www.googletagmanager.com/gtag/js?id=G-XXXX" async></script>
//
// Method B — inject from React after consent:
//   const { categories } = useConsent();
//   useEffect(() => {
//     if (categories.analytics !== 'granted') return;
//     // imperatively inject the tracker here
//   }, [categories.analytics]);
//
// When a real tracker is added, also update server.ts CSP scriptSrc/connectSrc
// to whitelist its domain (e.g. https://www.googletagmanager.com).

import type { ConsentCategory, ConsentState } from './types';

type GtagParams = {
  ad_storage?: 'granted' | 'denied';
  ad_user_data?: 'granted' | 'denied';
  ad_personalization?: 'granted' | 'denied';
  analytics_storage?: 'granted' | 'denied';
  functionality_storage?: 'granted' | 'denied';
  personalization_storage?: 'granted' | 'denied';
  security_storage?: 'granted' | 'denied';
};

declare global {
  interface Window {
    dataLayer: unknown[];
    gtag: (...args: unknown[]) => void;
  }
}

export const CONSENT_CHANGE_EVENT = 'markflow:consent-change';

export function categoriesToGtagParams(
  categories: Record<ConsentCategory, 'granted' | 'denied'>
): GtagParams {
  return {
    ad_storage: categories.marketing,
    ad_user_data: categories.marketing,
    ad_personalization: categories.marketing,
    analytics_storage: categories.analytics,
    functionality_storage: categories.functional,
    personalization_storage: categories.functional,
    security_storage: 'granted',
  };
}

function activateScripts(category: ConsentCategory): void {
  const selector = `script[type="text/plain"][data-consent="${category}"]:not([data-consent-activated])`;
  const nodes = document.querySelectorAll<HTMLScriptElement>(selector);
  nodes.forEach((old) => {
    const next = document.createElement('script');
    for (const attr of Array.from(old.attributes)) {
      if (attr.name === 'type') continue;
      next.setAttribute(attr.name, attr.value);
    }
    next.type = 'text/javascript';
    next.setAttribute('data-consent-activated', 'true');
    if (old.src) {
      next.src = old.src;
    } else {
      next.textContent = old.textContent ?? '';
    }
    old.parentNode?.replaceChild(next, old);
  });
}

export function applyConsent(state: ConsentState): void {
  if (typeof window === 'undefined') return;
  if (typeof window.gtag === 'function') {
    window.gtag('consent', 'update', categoriesToGtagParams(state.categories));
  }
  (['functional', 'analytics', 'marketing'] as const).forEach((cat) => {
    if (state.categories[cat] === 'granted') activateScripts(cat);
  });
  window.dispatchEvent(new CustomEvent(CONSENT_CHANGE_EVENT, { detail: state }));
}
